import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import LiveCard from './LiveCard';
import { liveApi } from '../../config/config';
import {
  isDemo, DEMO_PATIENTS,
  watchPatient, unwatchPatient, disconnectLive,
  isServerUp, onServerLink,
} from './telemetryStore';
import { armAudio, playBurst, isMuted, setMuted, onMuteChange } from './alertSound';
import { PRIORITIES, loadAlarmConfig } from './alarmConfig';
import AlarmSettings from './AlarmSettings';
import { useMissingData, MissingDataPanel } from './MissingData';
import {
  isPatientMuted, setPatientMuted, mutedPatientCount, onPatientMuteChange,
  isAlarmMuted, mutedAlarmCount, dropEndedAlarmMutes,
} from './patientMute';
import { reconcile, fetchWatchlist, pushWatchlist, watchlistSignature } from './watchlistSync';
import './livemonitor.css';

const STORAGE_KEY = 'lm-watchlist';
// Sounding is decided per alarm by its configured rule (alarmConfig): priority
// → burst pattern, category → melody, repeatSec → interburst interval,
// maxSoundSec → when the SOUND stops while the visual alarm stays (a phone that
// is off overnight must not sound all night).
const ANNUNCIATOR_TICK_MS = 250;
const BURST_GAP_MS = 700;      // silence between two bursts so they stay distinguishable

function fmtDuration(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, '0')}s`;
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
}

function loadWatchlist() {
  if (isDemo()) return DEMO_PATIENTS;
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
  } catch (e) {
    return [];
  }
}

/**
 * Live Monitor — /live-monitor  (add ?demo=1 for synthetic patients)
 *
 * Watch live patients as cards: 5 parameters + a streaming waveform each.
 * "Add patient" subscribes through the /api/live socket bridge; the list
 * persists in localStorage so the board survives a reload.
 */
export default function LiveMonitor() {
  const [watchlist, setWatchlist] = useState(loadWatchlist);
  const [statuses, setStatuses] = useState({});
  // Clinical alerts render on the cards themselves; this map drives the sound
  // (new-alert beeps + the 8s critical re-beep via alertsRef) and feeds the
  // separate Connection-alerts button (lead off / device off / disconnected).
  const [alertsMap, setAlertsMap] = useState({});
  // Acknowledged connection alerts: "deviceId:key" -> the alert's `since`.
  // Keyed by `since` so the SAME problem recurring later alarms again.
  const [acked, setAcked] = useState({});
  const ackedRef = useRef({});
  const [showConn, setShowConn] = useState(false);
  const [showMissing, setShowMissing] = useState(false);
  const missing = useMissingData(watchlist.map(p => String(p.deviceId)));
  const [showSettings, setShowSettings] = useState(false);
  const [mutedCount, setMutedCount] = useState(mutedPatientCount);   // patients with their own sound off
  const [mutedAlarms, setMutedAlarms] = useState(mutedAlarmCount);   // single alarms silenced on a card
  const burstRef = useRef({ last: {}, busyUntil: 0 });  // annunciator state
  const [serverUp, setServerUp] = useState(isServerUp);
  const [muted, setMutedState] = useState(isMuted);
  const [known, setKnown] = useState([]);        // patients from /api/live/patients
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState('');        // patient search text
  // Looking a patient ID up on this server, then on the master (web).
  const [lookup, setLookup] = useState({ busy: false, error: '' });
  const [clock, setClock] = useState(() => new Date());
  const alertsRef = useRef({});                  // mirror for the re-beep interval

  // Server-side copy of the board (per user) — see watchlistSync.js. Nothing is
  // SAVED to the server until its copy has been LOADED: right after a login the
  // browser copy is empty (logout wipes localStorage), and saving that would
  // erase the very list we are about to restore.
  const watchlistRef = useRef(watchlist);
  const initialLocalRef = useRef(watchlist);
  const removedBeforeLoadRef = useRef([]);
  const serverReadyRef = useRef(false);
  const lastSavedRef = useRef('');
  const saveTimerRef = useRef(null);
  // Shown in the header so a save that fails is never silent.
  //   loading | saved | saving | local (route not deployed) | error
  const [sync, setSync] = useState({ state: isDemo() ? 'demo' : 'loading', detail: '' });
  const describeError = (err) => {
    const status = err && err.response && err.response.status;
    if (status === 401) return 'not signed in (401) — log in again';
    if (status === 404) return 'server has no watchlist route (404) — backend not updated';
    if (status) return `server error ${status}`;
    return 'server unreachable or blocked (network / CORS)';
  };

  // Subscribe everything on the watchlist; the store dedupes.
  useEffect(() => {
    watchlistRef.current = watchlist;
    watchlist.forEach(p => watchPatient(p.deviceId, p.patientId));
    if (isDemo()) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(watchlist));
    if (!serverReadyRef.current) return;
    const sig = watchlistSignature(watchlist);
    if (sig === lastSavedRef.current) return;
    clearTimeout(saveTimerRef.current);
    setSync({ state: 'saving', detail: '' });
    saveTimerRef.current = setTimeout(() => {
      const sent = watchlistSignature(watchlistRef.current);
      pushWatchlist(watchlistRef.current)
        .then(() => { lastSavedRef.current = sent; setSync({ state: 'saved', detail: '' }); })
        .catch((err) => {
          // Not saved: say so, and try again — the list must not be lost quietly.
          setSync({ state: 'error', detail: describeError(err) });
          clearTimeout(saveTimerRef.current);
          saveTimerRef.current = setTimeout(() => {
            if (watchlistSignature(watchlistRef.current) === lastSavedRef.current) return;
            pushWatchlist(watchlistRef.current)
              .then(() => { lastSavedRef.current = watchlistSignature(watchlistRef.current); setSync({ state: 'saved', detail: '' }); })
              .catch((e2) => setSync({ state: 'error', detail: describeError(e2) }));
          }, 10000);
        });
    }, 400);
  }, [watchlist]);

  useEffect(() => {
    if (isDemo()) return undefined;
    let cancelled = false;
    let retry = null;
    const load = () => {
      fetchWatchlist().then(server => {
        if (cancelled) return;
        const r = reconcile(server, watchlistRef.current, initialLocalRef.current, removedBeforeLoadRef.current);
        serverReadyRef.current = true;
        // If nothing needs pushing, what the server has IS what we show — mark it
        // saved so the effect above doesn't immediately write it back.
        lastSavedRef.current = r.push ? '' : watchlistSignature(r.list);
        setSync({ state: r.push ? 'saving' : 'saved', detail: '' });
        setWatchlist(r.list);
      }).catch(err => {
        if (cancelled) return;
        const status = err && err.response && err.response.status;
        if (status === 404) { setSync({ state: 'local', detail: describeError(err) }); return; } // browser copy only
        setSync({ state: 'error', detail: describeError(err) });
        retry = setTimeout(load, 30000);            // try again, never save blind
      });
    };
    load();
    return () => { cancelled = true; clearTimeout(retry); clearTimeout(saveTimerRef.current); };
  }, []);

  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    if (!isDemo()) {
      axios.get(`${liveApi}api/live/patients`)
        .then(res => setKnown(Array.isArray(res.data) ? res.data : []))
        .catch(() => setKnown([])); // picker just falls back to manual entry
    }
    // Browsers keep audio suspended until the user interacts — arm on first input.
    const arm = () => armAudio();
    document.addEventListener('click', arm);
    document.addEventListener('keydown', arm);
    // Re-beep while any critical alert stays active (standard monitor behaviour).
    if (!isDemo()) loadAlarmConfig();   // shared rule set; falls back to the local copy

    // THE ANNUNCIATOR — one sound source for the whole board (IEC 60601-1-8:
    // with several alarm conditions present, the highest priority signal takes
    // precedence; signals must not pile on top of each other). Every tick it
    // finds the alarms that are due to sound and plays the burst of the most
    // urgent one, then waits for it to finish.
    const rebeep = setInterval(() => {
      if (isMuted()) return;
      const now = Date.now();
      const st = burstRef.current;
      if (now < st.busyUntil) return;
      const live = {};
      const due = [];
      Object.keys(alertsRef.current).forEach(dev => {
        const patientMuted = isPatientMuted(dev);
        alertsRef.current[dev].forEach(a => {
          const id = `${dev}:${a.key}:${a.since}`;
          live[id] = true;
          // This patient's sound is off (bell on their card). Not marked as
          // sounded, so an alarm still active when they are unmuted sounds at once.
          if (patientMuted) return;
          if (a.audible === false) return;                  // this alarm is muted for everyone (Alarm settings)
          if (isAlarmMuted(dev, a.key)) return;             // this alarm was silenced on this patient's card
          if (a.quiet) return;                                           // a repeat that already sounded
          if (ackedRef.current[`${dev}:${a.key}`] === a.since) return;   // acknowledged
          if (a.maxSoundSec > 0 && now - a.since > a.maxSoundSec * 1000) return; // sound limit reached
          const last = st.last[id];
          if (last != null && (!(a.repeatSec > 0) || now - last < a.repeatSec * 1000)) return;
          due.push({ id, a, last: last || 0, rank: (PRIORITIES[a.priority] || PRIORITIES.medium).rank });
        });
      });
      Object.keys(st.last).forEach(id => { if (!live[id]) delete st.last[id]; });
      if (!due.length) return;
      due.sort((x, y) => x.rank - y.rank || x.last - y.last);
      const pick = due[0];
      const ms = playBurst(pick.a.priority, pick.a.sound);
      if (!ms) return;                       // audio not armed yet — try again next tick
      st.busyUntil = now + ms + BURST_GAP_MS;
      // The same signal due for another patient is covered by this one burst.
      due.forEach(d => {
        if (d.a.priority === pick.a.priority && d.a.sound === pick.a.sound) st.last[d.id] = now;
      });
    }, ANNUNCIATOR_TICK_MS);
    const offLink = onServerLink(setServerUp);
    const offMute = onMuteChange(setMutedState);   // mute pressed in another tab/window
    const offPatientMute = onPatientMuteChange(() => { setMutedCount(mutedPatientCount()); setMutedAlarms(mutedAlarmCount()); });
    // Another screen may change the alarm settings: follow them without a reload.
    const cfgTimer = isDemo() ? null : setInterval(loadAlarmConfig, 60000);
    return () => {
      clearInterval(t);
      clearInterval(rebeep);
      offLink();
      offMute();
      offPatientMute();
      clearInterval(cfgTimer);
      document.removeEventListener('click', arm);
      document.removeEventListener('keydown', arm);
      disconnectLive();
    };
  }, []);

  // ── Critical-alarm acknowledgment ──
  // The server's alarm record decides what needs an answer: every critical
  // patient alarm of the last 12 h that nobody has acknowledged yet. Polled
  // so an acknowledgment made on another screen shows here too.
  const [critical, setCritical] = useState({});        // deviceId -> [alarm]
  const watchedIds = watchlist.map(p => String(p.deviceId)).join(',');
  const loadCritical = useCallback(() => {
    if (isDemo()) {
      // Demo board: one sample critical alarm on the first patient, answered locally.
      const first = watchedIds.split(',')[0];
      setCritical(prev => (first && !prev[first]
        ? { [first]: [{ id: 'demo-1', deviceId: first, code: 'ev_vt', label: 'VT', onset: Date.now() - 90 * 1000, cleared: null, ack: null }] }
        : prev));
      return;
    }
    if (!watchedIds) { setCritical({}); return; }
    axios.get(`${liveApi}api/live/critical`, { params: { devices: watchedIds } })
      .then(res => {
        const by = {};
        ((res.data && res.data.alarms) || []).forEach(a => { (by[a.deviceId] = by[a.deviceId] || []).push(a); });
        setCritical(by);
      })
      .catch(() => {});   // an older server without the route simply shows no acknowledgments
  }, [watchedIds]);
  useEffect(() => {
    loadCritical();
    const t = setInterval(loadCritical, 10000);
    return () => clearInterval(t);
  }, [loadCritical]);
  const ackAlarm = useCallback((alarm) => (
    (isDemo()
      ? Promise.resolve({ ...alarm, ack: { by: localStorage.getItem('verifiername') || 'you', at: new Date().toISOString(),
          responseSec: Math.round((Date.now() - alarm.onset) / 1000) } })
      : axios.post(`${liveApi}api/live/alerts/${alarm.id}/ack`).then(res => res.data.alarm))
      .catch(err => {
        if (err.response && err.response.status === 409 && err.response.data.alarm) return err.response.data.alarm;  // someone else answered first
        throw new Error((err.response && err.response.data && err.response.data.error) || 'Could not save the acknowledgment');
      })
      .then(saved => {
        setCritical(prev => ({
          ...prev,
          [saved.deviceId]: (prev[saved.deviceId] || []).map(a => (a.id === saved.id ? saved : a)),
        }));
        return saved;
      })
  ), []);

  const onStatusChange = useCallback((deviceId, status) => {
    setStatuses(prev => (prev[deviceId] === status ? prev : { ...prev, [deviceId]: status }));
  }, []);

  const onAlertsChange = useCallback((deviceId, alerts) => {
    // A mute placed on one alarm lasts for that episode: once it clears, drop it.
    dropEndedAlarmMutes(deviceId, alerts.map(a => a.key));
    setAlertsMap(prev => {
      const before = prev[deviceId] || [];
      // Sound is the annunciator's job: a new alarm has no "last burst" yet,
      // so it is due on the next tick (≤ 250 ms).
      const stamped = alerts.map(a => {
        const old = before.find(b => b.key === a.key);
        return { ...a, since: old ? old.since : Date.now() };
      });
      const next = { ...prev, [deviceId]: stamped };
      if (!alerts.length) delete next[deviceId];
      alertsRef.current = next;
      return next;
    });
  }, []);

  const ackKey = (deviceId, a) => `${deviceId}:${a.key}`;
  const isAcked = (deviceId, a) => acked[ackKey(deviceId, a)] === a.since;
  const acknowledge = (items) => {
    setAcked(prev => {
      const next = { ...prev };
      items.forEach(it => { next[ackKey(it.deviceId, it)] = it.since; });
      ackedRef.current = next;
      return next;
    });
  };
  const goToCard = (deviceId) => {
    const el = document.getElementById(`lm-card-${deviceId}`);
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const toggleMute = () => {
    armAudio();
    const m = !isMuted();
    setMuted(m);
    setMutedState(m);
  };

  const isWatched = (deviceId) => watchlist.some(p => p.deviceId === deviceId);

  const addEntry = (entry) => {
    setWatchlist(prev => (prev.some(p => p.deviceId === entry.deviceId) ? prev : [...prev, entry]));
    setQuery('');
    setAdding(false);
  };

  // Add a real, registered patient from the search results. Carry the fields
  // the per-card History link needs (analysed status + dataset id).
  const addKnown = (p) => addEntry({
    deviceId: p.deviceId,
    patientId: p.patientId,
    name: p.name,
    reportStatus: p.reportStatus,
    lastDatasetId: p.lastDatasetId,
    ward: p.ward || '',
    bed: p.bed || '',
  });

  // Add by PATIENT ID when the picker has no match. Patients register on the
  // master (web); this server may not hold the record yet. The backend looks
  // here first and otherwise copies the master's record across, so the card
  // gets the real name / patientId / history link instead of a bare device ID.
  const importById = () => {
    const id = query.trim();
    if (!/^\d+$/.test(id) || lookup.busy) return;
    setLookup({ busy: true, error: '' });
    axios.post(`${liveApi}api/live/patients/import`, { id })
      .then(res => {
        const p = res.data && res.data.patient;
        if (!p || !p.deviceId) throw new Error('unexpected response');
        setKnown(prev => (prev.some(k => k.deviceId === p.deviceId) ? prev : [...prev, p]));
        setLookup({ busy: false, error: '' });
        addKnown(p);
      })
      .catch(err => {
        const msg = (err.response && err.response.data && err.response.data.error)
          || (err.response ? `Lookup failed (HTTP ${err.response.status})` : 'Lookup failed — server unreachable');
        setLookup({ busy: false, error: msg });
      });
  };

  // Fallback: watch a raw device ID that isn't a registered patient
  // (unregistered device, or a test/simulator device like SIM-TLS).
  const addRaw = () => {
    const id = query.trim();
    if (id) addEntry({ deviceId: id, patientId: '', name: '' });
  };

  // Search over name, patient ID, and device ID. Nothing shows until the user
  // types — clicking the empty box should not dump the whole patient list.
  const q = query.trim().toLowerCase();
  const results = (q
    ? known.filter(p =>
        (p.name && p.name.toLowerCase().includes(q)) ||
        (p.deviceId && p.deviceId.toLowerCase().includes(q)) ||
        (p.patientId && p.patientId.toLowerCase().includes(q)) ||
        (p.ward && p.ward.toLowerCase().includes(q)) ||
        (p.bed && p.bed.toLowerCase().includes(q)))
    : []
  ).slice(0, 8);
  const exactDevice = known.some(p => p.deviceId === query.trim());
  // A number that matches no patient here is a patient ID worth asking the master about.
  const canImport = /^\d+$/.test(query.trim()) && !exactDevice;

  const onSearchKey = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (results.length) addKnown(results[0]);
      else if (canImport) importById();
      else addRaw();
    } else if (e.key === 'Escape') {
      setAdding(false);
      setQuery('');
    }
  };

  // Ward + bed. Saved on the patient record so every viewer sees the same
  // location; also kept on this browser's watchlist entry so it still works for
  // an unregistered/test device, or before the backend route is deployed.
  const saveLocation = useCallback((patient, ward, bed) => {
    const w = String(ward || '').trim().slice(0, 24);
    const b = String(bed || '').trim().slice(0, 24);
    setWatchlist(prev => prev.map(p => (p.deviceId === patient.deviceId ? { ...p, ward: w, bed: b } : p)));
    setKnown(prev => prev.map(k => (k.deviceId === patient.deviceId && 'ward' in k ? { ...k, ward: w, bed: b } : k)));
    if (!isDemo() && patient.patientId) {
      axios.post(`${liveApi}api/live/location`, { patientId: patient.patientId, ward: w, bed: b })
        .catch(() => { /* older backend / offline: the local copy above still shows it */ });
    }
  }, []);

  const removePatient = useCallback((patient) => {
    if (!serverReadyRef.current) removedBeforeLoadRef.current.push(patient.deviceId);
    setPatientMuted(patient.deviceId, false);   // re-adding this patient later starts with sound on
    unwatchPatient(patient.deviceId);
    setWatchlist(prev => prev.filter(p => p.deviceId !== patient.deviceId));
    setStatuses(prev => {
      const next = { ...prev };
      delete next[patient.deviceId];
      return next;
    });
    setAlertsMap(prev => {
      const next = { ...prev };
      delete next[patient.deviceId];
      alertsRef.current = next;
      return next;
    });
  }, []);

  // Merge the freshest patient record (analysed status can change over time)
  // over the persisted watchlist entry before rendering.
  const knownByDevice = {};
  known.forEach(k => { knownByDevice[k.deviceId] = k; });
  // FIXED ORDER: cards stay where they were added — no re-sorting on status.
  // A card jumping position when a device goes offline/online is disruptive;
  // urgency is already carried by the blinking border, alert tags, sound, and
  // the header counts.
  const sorted = watchlist.map(p => {
    const k = knownByDevice[p.deviceId];
    if (!k) return p;
    // The server is the source of truth for ward/bed once it knows the fields
    // (so an edit made by another viewer shows up here); otherwise fall back to
    // this browser's saved copy.
    const serverHasLoc = 'ward' in k || 'bed' in k;
    return { ...p, name: p.name || k.name, patientId: p.patientId || k.patientId,
      reportStatus: k.reportStatus, lastDatasetId: k.lastDatasetId,
      ward: serverHasLoc ? (k.ward || '') : (p.ward || ''),
      bed: serverHasLoc ? (k.bed || '') : (p.bed || '') };
  });

  // Connection alerts across the board, oldest first (longest unmonitored on top).
  const nameOf = {}, locOf = {};
  sorted.forEach(p => {
    nameOf[p.deviceId] = p.name || '';
    locOf[p.deviceId] = [p.ward ? `Ward ${p.ward}` : '', p.bed ? `Bed ${p.bed}` : ''].filter(Boolean).join(' · ');
  });
  const connList = [];
  Object.keys(alertsMap).forEach(deviceId => {
    (alertsMap[deviceId] || []).forEach(a => {
      if (a.conn) connList.push({ ...a, deviceId, name: nameOf[deviceId], loc: locOf[deviceId], acked: isAcked(deviceId, a) });
    });
  });
  connList.sort((a, b) => a.since - b.since);
  const connOpen = connList.filter(c => !c.acked);
  const connCritical = connOpen.some(c => c.severity === 'critical');
  const ackedAllFor = (deviceId) => {
    const list = (alertsMap[deviceId] || []).filter(a => a.conn);
    return list.length > 0 && list.every(a => isAcked(deviceId, a));
  };

  const totals = { critical: 0, warning: 0, stale: 0 };
  watchlist.forEach(p => {
    const s = statuses[p.deviceId];
    if (totals[s] != null) totals[s]++;
  });


  return (
    <div className="lm-page">
      <header className="lm-topbar">
        <div>
          <h1 className="lm-title">Live Monitor{isDemo() ? ' — demo' : ''}</h1>
          <div className="lm-subtitle">
            {watchlist.length} patients · {totals.critical} critical ·{' '}
            {totals.warning} warning · {totals.stale} offline
            {mutedAlarms > 0 && !muted && (
              <span className="lm-mutedcount" title="Single alarms silenced from a patient's card. Each comes back on its own when that alarm clears; click the alarm again to unmute it now.">
                {mutedAlarms} {mutedAlarms === 1 ? 'alarm' : 'alarms'} muted
              </span>
            )}
            {mutedCount > 0 && !muted && (
              <span className="lm-mutedcount" title="These patients' alarms still show on their cards but make no sound. Use the bell on a card to turn sound back on.">
                {mutedCount} {mutedCount === 1 ? 'patient has' : 'patients have'} sound off
              </span>
            )}
            {sync.state !== 'demo' && (
              <span
                className={`lm-sync lm-sync-${sync.state}`}
                title={sync.state === 'saved'
                  ? 'This list is saved to your account. It comes back after logout and on any computer, until you remove a patient.'
                  : (sync.detail || '')}
              >
                {sync.state === 'loading' && 'Loading your list…'}
                {sync.state === 'saving' && 'Saving…'}
                {sync.state === 'saved' && 'List saved to your account'}
                {sync.state === 'local' && `List NOT saved to your account — ${sync.detail}. It will be lost at logout.`}
                {sync.state === 'error' && `List NOT saved — ${sync.detail}. Retrying…`}
              </span>
            )}
          </div>
        </div>
        <div className="lm-topbar-right">
          <div className="lm-clock">{clock.toLocaleTimeString()}</div>
          <button
            type="button"
            className={`lm-add-btn lm-conn-btn${connOpen.length ? (connCritical ? ' lm-conn-crit' : ' lm-conn-warn') : ''}${showConn ? ' lm-open' : ''}`}
            onClick={() => setShowConn(v => !v)}
            aria-expanded={showConn}
            title="Lead-off, device-off and disconnected patients"
          >
            Connection alerts
            <span className="lm-conn-count">{connOpen.length}</span>
          </button>
          {!isDemo() && (
            <button
              type="button"
              className={`lm-add-btn lm-conn-btn${missing.open.length ? ' lm-conn-crit' : ''}${showMissing ? ' lm-open' : ''}`}
              onClick={() => { setShowMissing(v => !v); missing.reload(); }}
              aria-expanded={showMissing}
              title="Patients on this board whose ECG is not reaching the server (gap, no new ECG, waiting on the phone, not processed)"
            >
              Missing data
              <span className="lm-conn-count">{missing.open.length}</span>
            </button>
          )}
          <button
            type="button"
            className={`lm-add-btn${showSettings ? ' lm-open' : ''}`}
            onClick={() => setShowSettings(v => !v)}
            aria-expanded={showSettings}
            title="Alarm limits, arrhythmia alarms, priorities and sounds"
          >
            Alarm settings
          </button>
          <button
            type="button"
            className={`lm-add-btn lm-mute-btn${muted ? ' lm-muted' : ''}`}
            onClick={toggleMute}
            title={muted ? 'All alarm sound is off for every patient. Click to turn sound back on.' : 'Alarm sound is on. Click to mute ALL patients. To mute one patient, use the bell on their card.'}
          >
            {muted ? '🔇 All muted' : '🔔 Sound on'}
          </button>
          <button type="button" className="lm-add-btn" onClick={() => setAdding(a => !a)}>
            {adding ? 'Cancel' : '+ Add patient'}
          </button>
        </div>
      </header>


      {!serverUp && (
        <div className="lm-server-down" role="alert">
          This dashboard lost its connection to the server — live data is paused and
          patient alarms are suspended until it reconnects. Reconnecting…
        </div>
      )}

      {showSettings && <AlarmSettings onClose={() => setShowSettings(false)} />}

      {showMissing && <MissingDataPanel data={missing} onShowCard={goToCard} />}

      {showConn && (
        <div className="lm-conn-panel">
          <div className="lm-conn-head">
            <span>
              {connList.length
                ? `${connOpen.length} open · ${connList.length - connOpen.length} acknowledged`
                : 'No lead-off, device-off or disconnected patients.'}
            </span>
            {connOpen.length > 1 && (
              <button type="button" className="lm-conn-ack" onClick={() => acknowledge(connOpen)}>
                Acknowledge all
              </button>
            )}
          </div>
          {connList.map(c => (
            <div key={`${c.deviceId}:${c.key}`} className={`lm-conn-row lm-conn-row-${c.severity}${c.acked ? ' lm-conn-row-acked' : ''}`}>
              <span className={`lm-alert-tag lm-alert-${c.severity}`}>
                {c.mark ? <span className="lm-alert-mark">{c.mark}</span> : null}{c.label}
              </span>
              <button type="button" className="lm-conn-who" onClick={() => goToCard(c.deviceId)} title="Show this patient's card">
                <span className="lm-conn-name">
                  {c.name || c.deviceId}
                  {c.loc ? <span className="lm-conn-loc">{c.loc}</span> : null}
                </span>
                <span className="lm-conn-meta">{c.name ? `${c.deviceId} · ` : ''}{c.text}</span>
              </button>
              <span className="lm-conn-for" title={`Since ${new Date(c.since).toLocaleTimeString()}`}>
                {fmtDuration(clock.getTime() - c.since)}
              </span>
              {c.acked
                ? <span className="lm-conn-acked">acknowledged</span>
                : <button type="button" className="lm-conn-ack" onClick={() => acknowledge([c])}>Acknowledge</button>}
            </div>
          ))}
        </div>
      )}

      {adding && (
        <div className="lm-add-panel">
          <input
            className="lm-add-input"
            value={query}
            onChange={e => { setQuery(e.target.value); if (lookup.error) setLookup({ busy: false, error: '' }); }}
            onKeyDown={onSearchKey}
            placeholder="Search patient by name, patient ID, or device ID"
            autoFocus
          />
          {q && (
            <div className="lm-add-results">
              {results.map(p => {
                const already = isWatched(p.deviceId);
                return (
                  <button
                    key={p.deviceId}
                    type="button"
                    className="lm-add-row"
                    disabled={already}
                    onClick={() => addKnown(p)}
                  >
                    <span className="lm-add-name">{p.name || '(no name)'}</span>
                    <span className="lm-add-meta">
                      {p.deviceId}{p.patientId ? ` · ID ${p.patientId}` : ''}
                    </span>
                    {already && <span className="lm-add-already">watching</span>}
                  </button>
                );
              })}

              {canImport && (
                <button type="button" className="lm-add-row lm-add-import" disabled={lookup.busy} onClick={importById}>
                  <span className="lm-add-name">
                    {lookup.busy ? `Looking up patient ${query.trim()}…` : `Find patient ID “${query.trim()}”`}
                  </span>
                  <span className="lm-add-meta">not on this server — fetch details from the master (web) and add</span>
                </button>
              )}
              {lookup.error && <div className="lm-add-error" role="alert">{lookup.error}</div>}

              {!exactDevice && (
                <button type="button" className="lm-add-row lm-add-raw" onClick={addRaw}>
                  <span className="lm-add-name">Watch device “{query.trim()}”</span>
                  <span className="lm-add-meta">unregistered / test device</span>
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {watchlist.length === 0 ? (
        <div className="lm-empty">
          No patients on the board yet. Use <strong>+ Add patient</strong> to start
          watching a live device{isDemo() ? '' : ', or open ?demo=1 for a demo feed'}.
        </div>
      ) : (
        <div className="lm-grid">
          {sorted.map(p => (
            <LiveCard
              key={p.deviceId}
              patient={p}
              onStatusChange={onStatusChange}
              onAlertsChange={onAlertsChange}
              onRemove={removePatient}
              onLocationChange={saveLocation}
              connAcked={ackedAllFor(p.deviceId)}
              critical={critical[String(p.deviceId)]}
              onAck={ackAlarm}
            />
          ))}
        </div>
      )}
    </div>
  );
}
